import { useCallback } from "react";
import { ChatMessage } from "../types/chat";
import {
  makeCompareRequest,
  preFetchMediaAndAudio,
} from "../services/ChatService";
import {
  getLastBotMessage,
  buildActionsFromLastBotMessage,
  extractLinksFromMessage,
} from "../utils/messageUtils";
import { CHAT_CONSTANTS } from "../utils/constants";
import { useAuth } from "../providers/AuthContext";
import { useMessages } from "../providers/MessagesContext";
import { useChatActions } from "./useChatActions";
import { wrapTyping, shouldBlockActions } from "../utils/typingGate";
import { debuggerService } from "../services/DebuggerService";
import { getCurrentSessionId } from "../utils/apiUtils";

interface UseHandleCompareRequestProps {
  audio: {
    play: (id: string) => Promise<void>;
    setFullscreenVideoMessageId: (messageId: string) => void;
  };
}

export const useHandleCompareRequest = ({
  audio,
}: UseHandleCompareRequestProps) => {
  const { getUserEmail } = useAuth();
  const {
    addMessage,
    addMessageObject,
    setNewDecisionMessage,
    addStillHavingProblemOption,
    getLastVideoMessageId,
    addToStack,
    clearStack,
    messagesRef,
    goBackStackRef,
    getPrunedForSend,
    getMessageIds,
    sessionCreateTime,
  } = useMessages();

  // Import handleGoBack and handleRepeat from useChatActions
  const { handleGoBack, handleRepeat } = useChatActions();

  const handleCompareRequest = useCallback(
    async (userMessage: string) => {
      if (!userMessage.trim()) return;
      if (shouldBlockActions()) return;

      const userMessageId = crypto.randomUUID().toString();

      // Check if this is the second message (no message history)
      const isFirstMessage = messagesRef.current.length === 1;

      // If no message history, mark session as having a message
      if (isFirstMessage) {
        try {
          const sessionId = getCurrentSessionId();
          const userEmail = getUserEmail();
          await debuggerService.logSession({
            session_id: sessionId,
            user_email: userEmail,
            session_create_time: sessionCreateTime,
            terms_accepted: true,
            has_a_message: "true",
          });
        } catch (error) {
          console.error("Failed to update session with has_a_message:", error);
        }
      }

      addMessage(userMessage, "user", userMessageId);

      await wrapTyping(async () => {
        try {
          const lastBotMessage = getLastBotMessage(messagesRef.current);
          const nodeuuid = lastBotMessage?.nodeUuid || "";
          const canGoBack = goBackStackRef.current.length >= 2;
          const actions = buildActionsFromLastBotMessage(
            canGoBack,
            goBackStackRef.current,
            messagesRef.current
          );
          const processedOptions = addStillHavingProblemOption(
            lastBotMessage?.options || [],
            lastBotMessage?.text || ""
          );
          const links = lastBotMessage
            ? extractLinksFromMessage(lastBotMessage)
            : [];

          const response = await makeCompareRequest({
            options: processedOptions,
            nodeUUID: nodeuuid,
            userMessage,
            queryId: userMessageId,
            actions: actions,
            links: links,
            userEmail: getUserEmail(),
            chatHistory: getPrunedForSend(), // TODO: add chat history
            idHistory: getMessageIds(), // TODO: add id history
          });

          if ("action" in response && response.action) {
            if (response.action === "go back")
              await handleGoBack(userMessageId);
            else if (response.action === "repeat")
              await handleRepeat(userMessageId);
            else if (response.action === "play video") {
              const lastVideoMsgId = getLastVideoMessageId();
              if (lastVideoMsgId) {
                audio.play(
                  lastVideoMsgId.startsWith("vid-")
                    ? lastVideoMsgId
                    : "vid-" + lastVideoMsgId
                );
                audio.setFullscreenVideoMessageId(lastVideoMsgId);
              } else {
                console.error("No last video message id found !!!");
              }
              // }
            }
            return;
          } else if (response && "nodeUuid" in response) {
            setNewDecisionMessage(nodeuuid, response.nodeUuid || "");
          }

          let chatMessageResponse = response as ChatMessage;

          const responseWithPreFetched =
            await preFetchMediaAndAudio(chatMessageResponse);

          addToStack(responseWithPreFetched.nodeUuid || "", userMessageId);
          addMessageObject(responseWithPreFetched);
        } catch (error) {
          console.error("Compare request error:", error);
          addMessage(
            CHAT_CONSTANTS.ERROR_MESSAGE,
            "bot",
            userMessageId,
            "",
            [],
            "00000000-0000-0000-0000-000000000000",
            [],
            "Compare Request Error"
          );
        }
      });
    },
    [
      addMessage,
      addMessageObject,
      addToStack,
      addStillHavingProblemOption,
      setNewDecisionMessage,
      handleGoBack,
      handleRepeat,
      audio,
      getLastVideoMessageId,
      getUserEmail,
      clearStack,
      messagesRef,
      goBackStackRef,
      getPrunedForSend,
      getMessageIds,
      sessionCreateTime,
    ]
  );

  return { handleCompareRequest };
};

export default useHandleCompareRequest;
